import { useState } from "react";
import { Modal } from "react-bootstrap";
import { ModalBase } from "../../utils/modal_launcher";
import { useApiMutation } from "../../utils/api";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { genericError } from "../../utils/errors";

const roundToNearest5Minutes = (date: Date): Date => {
  const rounded = new Date(date);
  const minutes = rounded.getMinutes();
  const remainder = minutes % 5;
  rounded.setMinutes(minutes - remainder);
  rounded.setSeconds(0);
  rounded.setMilliseconds(0);
  return rounded;
};

const formatTime = (date: Date): string => {
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const STEP_MINUTES = 5;
const SKIP_MINUTES = 30;

interface LogActivityModalProps {
  model: string;
  close: () => void;
  refetch: () => void;
}

const titleize = (model: string): string => {
  return (
    model.charAt(0).toUpperCase() +
    model.slice(1).replace(/(?:_|-)([a-z])/g, (_, c) => " " + c.toUpperCase())
  );
};

export const LogActivityModal = ({
  model,
  close,
  refetch,
}: LogActivityModalProps) => {
  const title = titleize(model);
  const { post, saving } = useApiMutation();
  const [offsetMinutes, setOffsetMinutes] = useState(0);

  const baseTime = roundToNearest5Minutes(new Date());
  const selectedTime = new Date(baseTime.getTime() + offsetMinutes * 60 * 1000);

  const adjustTime = (delta: number) => {
    setOffsetMinutes(offsetMinutes + delta);
  };

  const handleSubmit = () => {
    post(`/api/user/create_${model}`, {
      ended_at: selectedTime.toISOString(),
    }).then(({ data }) => {
      if (data.success) {
        refetch();
        close();
      } else {
        genericError();
      }
    });
  };

  return (
    <ModalBase title={`Add ${title}`} close={close}>
      <Modal.Body className="text-center py-4">
        <div className="d-flex align-items-center justify-content-center gap-2 mb-4">
          <button
            type="button"
            className="btn btn-text btn-lg"
            onClick={() => adjustTime(-SKIP_MINUTES)}
          >
            <ChevronsLeft size={32} />
          </button>
          <button
            type="button"
            className="btn btn-text btn-lg"
            onClick={() => adjustTime(-STEP_MINUTES)}
          >
            <ChevronLeft size={32} />
          </button>

          <div className="px-4">
            <h2 className="mb-0 fw-bold">{formatTime(selectedTime)}</h2>
          </div>

          <button
            type="button"
            className="btn btn-text btn-lg"
            onClick={() => adjustTime(STEP_MINUTES)}
          >
            <ChevronRight size={32} />
          </button>
          <button
            type="button"
            className="btn btn-text btn-lg"
            onClick={() => adjustTime(SKIP_MINUTES)}
          >
            <ChevronsRight size={32} />
          </button>
        </div>

        <div className="d-flex mt-3">
          <button
            type="button"
            className="btn btn-primary btn-lg px-5 mx-auto"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </Modal.Body>
    </ModalBase>
  );
};
